import React, { useState, useCallback } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  HoverCard,
  Image,
  Loader,
  Text,
} from "@mantine/core";
import {
  IconLayoutGrid,
  IconList,
  IconCrosshair,
  IconUser,
  IconEye,
  IconEyeOff,
  IconTrash,
  IconArrowBackUp,
  IconSquareDashed,
  IconQuestionMark,
  IconX,
  IconChevronDown,
  IconChevronUp,
  IconPlus,
  IconReplace,
  IconMap,
} from "@tabler/icons-react";
import { UserDrawnBox } from "@labeling-tools/ui-components/src/box-draw-image/types";
import { PlanogramViewer } from "@labeling-tools/ui-components/src/planogram-viewer/planogram-viewer.comp";
import type { ProductSelection } from "@/types/coverage";
import type { BoxCategory } from "@/hooks/useCoverageState";
import { SelectItemPopup } from "@/components/coverage/SelectItemPopup";

export type RightPanelView = "planogram" | "detections";

export interface PlanogramSlotLabel {
  aisleIndex: number;
  planogramId: string;
  label: string;
}

interface CoverageRightPanelProps {
  // Planogram view
  planogramId?: string;
  planogramLoading?: boolean;

  // Multi-planogram tabs
  planogramSlots?: PlanogramSlotLabel[];
  activePlanogramTab?: number;
  onPlanogramTabChange?: (tab: number) => void;
  getBoxSlot?: (boxKey: string) => number;
  /** Short view name for a box, e.g. "View 2". Only set for grouped tasks. */
  getBoxViewLabel?: (boxKey: string) => string | undefined;

  // Detections view
  existingBoxes: { points: number[][] }[];
  userDrawnBoxes: UserDrawnBox[];
  hiddenExistingBoxIndices: Set<number>;
  hiddenUserDrawnBoxIndices: Set<number>;
  deletedBoxIndices: Set<number>;
  selectedExistingBoxIndex: number | null;
  selectedUserDrawnBoxIndex: number | null;
  productSelections: Map<string, ProductSelection>;
  getBoxKey: (index: number, isUserDrawn: boolean) => string;
  getBoxCategory: (boxKey: string) => BoxCategory;
  moveBoxToCategory: (boxKey: string, category: BoxCategory) => void;
  onBoxView: (index: number, isUserDrawn?: boolean) => void;
  onBoxDelete: (index: number, isUserDrawn?: boolean) => void;
  onBoxRestore: (index: number) => void;
  onBoxSelect: (index: number, isUserDrawn: boolean) => void;
  onRemoveSelection: (boxKey: string) => void;
  /** Map from product_id to image URL for showing product thumbnails */
  productImageUrls: Map<string, string>;
  /** Map from product_id to product name/description */
  productNames: Map<string, string>;
  /** Map from planogram_id to planogram name/description */
  planogramNames: Map<string, string>;
  /** Controlled view state */
  view: RightPanelView;
  onViewChange: (view: RightPanelView) => void;
  /** When true, UI is blocked waiting for product selection */
  isSelectionBlocked?: boolean;
  /** Stable display numbers for boxes (keyed by box index in combined array) */
  boxNumberLabels: Map<number, number>;
  /** Callback to open the planogram search modal */
  onIncorrectPlanogram?: () => void;
  /** Open modal with a specific slot pre-selected for replacement */
  onReplacePlanogram?: (slotIndex: number) => void;
  /** Open modal in add mode */
  onAddPlanogram?: () => void;
  /** Assign a box to a planogram slot */
  assignBoxToSlot?: (boxKey: string, slotIndex: number) => void;
  /** Currently selected product IDs for highlighting on the planogram */
  selectedProductIds?: string[];
  /** Product IDs to transiently highlight (teal) on the planogram — shelf-detection hover */
  highlightedProductIds?: string[];
  /** Product to pan/zoom the planogram to — shelf-detection click */
  focusProductId?: string | null;
  /** When true, all editing actions are disabled (admin review mode) */
  readOnly?: boolean;
  /** Callback to open the planogram search modal in view-only mode (admin) */
  onViewAllPlanograms?: () => void;
  /** Number of products currently selected in the pending selection */
  selectedCount?: number;
  /** Confirm the current product selection */
  onConfirmSelection?: () => void;
  /** Dismiss the current product selection */
  onDismissSelection?: () => void;
}

export const CoverageRightPanel = ({
  planogramId,
  planogramLoading,
  planogramSlots,
  activePlanogramTab = 0,
  onPlanogramTabChange,
  getBoxSlot,
  getBoxViewLabel,
  existingBoxes,
  userDrawnBoxes,
  hiddenExistingBoxIndices,
  hiddenUserDrawnBoxIndices,
  deletedBoxIndices,
  selectedExistingBoxIndex,
  selectedUserDrawnBoxIndex,
  productSelections,
  getBoxKey,
  getBoxCategory,
  moveBoxToCategory,
  onBoxView,
  onBoxDelete,
  onBoxRestore,
  onBoxSelect,
  onRemoveSelection,
  productImageUrls,
  productNames,
  planogramNames,
  boxNumberLabels,
  view,
  onViewChange,
  isSelectionBlocked,
  onReplacePlanogram,
  onAddPlanogram,
  assignBoxToSlot,
  selectedProductIds,
  highlightedProductIds,
  focusProductId,
  readOnly,
  onViewAllPlanograms,
  selectedCount = 0,
  onConfirmSelection,
  onDismissSelection,
}: CoverageRightPanelProps) => {
  const hasMultiplePlanograms = (planogramSlots?.length ?? 0) > 1;
  const [planogramListExpanded, setPlanogramListExpanded] = useState(true);

  return (
    <div className="h-full flex flex-col bg-white border-l border-gray-200">
      {/* Header with view toggle */}
      <div className="grid grid-cols-3 items-center px-4 py-1.5 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Text fw={600} size="sm">
            {view === "planogram" ? "Planogram" : "Detections"}
          </Text>
        </div>
        <div className="flex gap-1 justify-center" data-training="view-toggle">
          <ActionIcon
            size="sm"
            variant={view === "planogram" ? "filled" : "light"}
            color={view === "planogram" ? "blue" : "gray"}
            onClick={() => onViewChange("planogram")}
            title="Planogram view"
          >
            <IconLayoutGrid size={16} />
          </ActionIcon>
          <ActionIcon
            size="sm"
            variant={view === "detections" ? "filled" : "light"}
            color={view === "detections" ? "blue" : "gray"}
            onClick={() => onViewChange("detections")}
            title="Detections view"
          >
            <IconList size={16} />
          </ActionIcon>
        </div>
        <div />
      </div>

      {/* Inline product selection prompt */}
      {isSelectionBlocked && view === "planogram" && (
        <SelectItemPopup
          visible
          selectedCount={selectedCount}
          onConfirm={onConfirmSelection ?? (() => {})}
          onDismiss={onDismissSelection ?? (() => {})}
        />
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {view === "planogram" ? (
          <div className="h-full flex flex-col">
            {/* Collapsible planogram slot list */}
            {planogramSlots && planogramSlots.length > 0 && (
              <div
                className="flex-shrink-0 border-b border-gray-200"
                data-training="planogram-list"
              >
                <button
                  className="flex items-center justify-between w-full px-4 py-2 hover:bg-gray-50 transition-colors"
                  onClick={() => setPlanogramListExpanded((v) => !v)}
                >
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                    Planograms ({planogramSlots.length})
                  </Text>
                  {planogramListExpanded ? (
                    <IconChevronUp size={14} color="#9ca3af" />
                  ) : (
                    <IconChevronDown size={14} color="#9ca3af" />
                  )}
                </button>
                {planogramListExpanded && (
                  <div className="px-2 pb-2">
                    {planogramSlots.map((slot, i) => (
                      <div
                        key={slot.aisleIndex}
                        className={`flex items-center justify-between px-3 py-1.5 rounded-md cursor-pointer transition-colors mb-0.5 ${
                          i === activePlanogramTab
                            ? "bg-blue-50 border border-blue-200"
                            : "hover:bg-gray-50 border border-transparent"
                        }`}
                        onClick={() => onPlanogramTabChange?.(i)}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Text size="xs" fw={600} c="dimmed">
                            {i + 1}.
                          </Text>
                          <Text size="xs" fw={500} lineClamp={1}>
                            {slot.label}
                          </Text>
                        </div>
                        {!readOnly && (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="gray"
                            leftSection={<IconReplace size={12} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onReplacePlanogram?.(i);
                            }}
                          >
                            Replace
                          </Button>
                        )}
                      </div>
                    ))}
                    {!readOnly &&
                      onAddPlanogram &&
                      planogramSlots.length < 2 && (
                        <Button
                          size="compact-xs"
                          variant="light"
                          leftSection={<IconPlus size={12} />}
                          fullWidth
                          className="mt-1"
                          onClick={onAddPlanogram}
                        >
                          Add Planogram
                        </Button>
                      )}
                    {readOnly && onViewAllPlanograms && (
                      <Button
                        size="compact-xs"
                        variant="light"
                        leftSection={<IconLayoutGrid size={12} />}
                        fullWidth
                        className="mt-1"
                        onClick={onViewAllPlanograms}
                      >
                        View All Planograms
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Planogram viewer */}
            <div className="flex-1 min-h-0" data-training="planogram-viewer">
              <CoveragePlanogramView
                planogramId={planogramId}
                isLoading={planogramLoading}
                selectedProductIds={selectedProductIds}
                highlightedProductIds={highlightedProductIds}
                focusProductId={focusProductId}
              />
            </div>
          </div>
        ) : (
          <DetectionsView
            existingBoxes={existingBoxes}
            userDrawnBoxes={userDrawnBoxes}
            hiddenExistingBoxIndices={hiddenExistingBoxIndices}
            hiddenUserDrawnBoxIndices={hiddenUserDrawnBoxIndices}
            deletedBoxIndices={deletedBoxIndices}
            selectedExistingBoxIndex={selectedExistingBoxIndex}
            selectedUserDrawnBoxIndex={selectedUserDrawnBoxIndex}
            productSelections={productSelections}
            getBoxKey={getBoxKey}
            getBoxCategory={getBoxCategory}
            moveBoxToCategory={moveBoxToCategory}
            onBoxView={onBoxView}
            onBoxDelete={onBoxDelete}
            onBoxRestore={onBoxRestore}
            onBoxSelect={onBoxSelect}
            onRemoveSelection={onRemoveSelection}
            productImageUrls={productImageUrls}
            productNames={productNames}
            planogramNames={planogramNames}
            boxNumberLabels={boxNumberLabels}
            activePlanogramTab={activePlanogramTab}
            onPlanogramTabChange={onPlanogramTabChange}
            getBoxSlot={getBoxSlot}
            getBoxViewLabel={getBoxViewLabel}
            planogramSlots={planogramSlots}
            hasMultiplePlanograms={hasMultiplePlanograms}
            assignBoxToSlot={assignBoxToSlot}
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
};

// --- Planogram View (uses existing PlanogramViewer component) ---

const CoveragePlanogramView = ({
  planogramId,
  isLoading,
  selectedProductIds,
  highlightedProductIds,
  focusProductId,
}: {
  planogramId?: string;
  isLoading?: boolean;
  selectedProductIds?: string[];
  highlightedProductIds?: string[];
  focusProductId?: string | null;
}) => {
  if (isLoading) {
    return (
      <Center className="h-full">
        <Loader />
      </Center>
    );
  }

  if (!planogramId) {
    return (
      <div className="flex items-center justify-center h-full">
        <Text c="dimmed" size="sm">
          No planogram available
        </Text>
      </div>
    );
  }

  return (
    <div className="h-full [&>div]:!h-full">
      <PlanogramViewer
        planogram_id={planogramId}
        fitContent
        selectedProductIds={selectedProductIds}
        highlightedProductIds={highlightedProductIds}
        initialProductId={focusProductId}
        // Shelf-detection click should only pan/zoom, not open the product
        // popover the way a real planogram click does.
        zoomOnlyInitialProduct
      />
    </div>
  );
};

// --- Detections View ---

interface BoxItem {
  index: number;
  isUserDrawn: boolean;
  boxKey: string;
  displayIndex: number;
  isAuto: boolean;
  isDeleted: boolean;
  isHidden: boolean;
  isSelected: boolean;
  hasSelection: boolean;
  category: BoxCategory;
}

const DetectionsView = ({
  existingBoxes,
  userDrawnBoxes,
  hiddenExistingBoxIndices,
  hiddenUserDrawnBoxIndices,
  deletedBoxIndices,
  selectedExistingBoxIndex,
  selectedUserDrawnBoxIndex,
  productSelections,
  getBoxKey,
  getBoxCategory,
  moveBoxToCategory,
  onBoxView,
  onBoxDelete,
  onBoxRestore,
  onBoxSelect,
  onRemoveSelection,
  productImageUrls,
  productNames,
  planogramNames,
  boxNumberLabels,
  getBoxSlot,
  getBoxViewLabel,
  hasMultiplePlanograms = false,
  assignBoxToSlot,
  readOnly,
}: {
  existingBoxes: { points: number[][] }[];
  userDrawnBoxes: UserDrawnBox[];
  hiddenExistingBoxIndices: Set<number>;
  hiddenUserDrawnBoxIndices: Set<number>;
  deletedBoxIndices: Set<number>;
  selectedExistingBoxIndex: number | null;
  selectedUserDrawnBoxIndex: number | null;
  productSelections: Map<string, ProductSelection>;
  getBoxKey: (index: number, isUserDrawn: boolean) => string;
  getBoxCategory: (boxKey: string) => BoxCategory;
  moveBoxToCategory: (boxKey: string, category: BoxCategory) => void;
  onBoxView: (index: number, isUserDrawn?: boolean) => void;
  onBoxDelete: (index: number, isUserDrawn?: boolean) => void;
  onBoxRestore: (index: number) => void;
  onBoxSelect: (index: number, isUserDrawn: boolean) => void;
  onRemoveSelection: (boxKey: string) => void;
  productImageUrls: Map<string, string>;
  productNames: Map<string, string>;
  planogramNames: Map<string, string>;
  boxNumberLabels: Map<number, number>;
  activePlanogramTab?: number;
  onPlanogramTabChange?: (tab: number) => void;
  getBoxSlot?: (boxKey: string) => number;
  getBoxViewLabel?: (boxKey: string) => string | undefined;
  planogramSlots?: PlanogramSlotLabel[];
  hasMultiplePlanograms?: boolean;
  assignBoxToSlot?: (boxKey: string, slotIndex: number) => void;
  readOnly?: boolean;
}) => {
  const [dragOverSection, setDragOverSection] = useState<BoxCategory | null>(
    null,
  );
  const [expandedBoxKey, setExpandedBoxKey] = useState<string | null>(null);

  // Build all box items
  const existingCount = existingBoxes.length;
  const allBoxItems: BoxItem[] = [];

  // Existing (auto) boxes
  existingBoxes.forEach((_, index) => {
    const boxKey = getBoxKey(index, false);
    const isDeleted = deletedBoxIndices.has(index);
    allBoxItems.push({
      index,
      isUserDrawn: false,
      boxKey,
      displayIndex: boxNumberLabels.get(index) ?? index + 1,
      isAuto: true,
      isDeleted,
      isHidden: hiddenExistingBoxIndices.has(index),
      isSelected: selectedExistingBoxIndex === index && !isDeleted,
      hasSelection: productSelections.has(boxKey),
      category: getBoxCategory(boxKey),
    });
  });

  // User-drawn OOS boxes
  userDrawnBoxes.forEach((box, index) => {
    if (box.type !== "draw") return;
    const boxKey = getBoxKey(index, true);
    allBoxItems.push({
      index,
      isUserDrawn: true,
      boxKey,
      displayIndex:
        boxNumberLabels.get(existingCount + index) ?? existingCount + index + 1,
      isAuto: false,
      isDeleted: false,
      isHidden: hiddenUserDrawnBoxIndices.has(index),
      isSelected: selectedUserDrawnBoxIndex === index,
      hasSelection: productSelections.has(boxKey),
      category: getBoxCategory(boxKey),
    });
  });

  // Person boxes (separate, not categorizable) — always show all
  const personBoxes = userDrawnBoxes
    .map((box, index) => ({ box, index }))
    .filter(({ box }) => box.type === "draw-user");

  // Filter boxes by category
  const allOosConfirmedBoxes = allBoxItems.filter(
    (b) => !b.isDeleted && b.category === "oos_confirmed",
  );
  const emptyAreaBoxes = allBoxItems.filter(
    (b) => !b.isDeleted && b.category === "empty_area",
  );
  const unassignedBoxes = allBoxItems.filter(
    (b) => !b.isDeleted && b.category === "unassigned",
  );
  const deletedBoxes = allBoxItems.filter((b) => b.isDeleted);

  const handleDragStart = useCallback((e: React.DragEvent, boxKey: string) => {
    e.dataTransfer.setData("text/plain", boxKey);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, section: BoxCategory) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverSection(section);
    },
    [],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverSection(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, targetCategory: BoxCategory, slotIndex?: number) => {
      e.preventDefault();
      setDragOverSection(null);
      const boxKey = e.dataTransfer.getData("text/plain");
      if (boxKey) {
        moveBoxToCategory(boxKey, targetCategory);
        // Also assign to planogram slot when dropping into a specific slot's OOS zone
        if (slotIndex != null && assignBoxToSlot) {
          assignBoxToSlot(boxKey, slotIndex);
        }
      }
    },
    [moveBoxToCategory, assignBoxToSlot],
  );

  const renderBoxRow = (item: BoxItem) => {
    const selection = productSelections.get(item.boxKey);
    const productIds = selection?.product_ids ?? [];
    const selectedCount = productIds.length;

    // Get image URLs and names for all selected products
    const productImageUrlsList = productIds
      .map((id) => productImageUrls.get(id))
      .filter((url): url is string => !!url);
    const productNamesList = productIds.map(
      (id) => productNames.get(id) ?? "Unknown product",
    );

    const planogramName = selection
      ? planogramNames.get(selection.planogram_id)
      : undefined;
    // Planogram slot number (1-indexed) for the badge on OOS rows
    const slotNumber =
      hasMultiplePlanograms && getBoxSlot
        ? getBoxSlot(item.boxKey) + 1
        : undefined;

    // Check if there's a valid selection (at least one product)
    const hasValidSelection = selectedCount > 0;

    return (
      <DetectionRow
        key={item.boxKey}
        boxKey={item.boxKey}
        displayIndex={item.displayIndex}
        isAuto={item.isAuto}
        isDeleted={item.isDeleted}
        isHidden={item.isHidden}
        isSelected={item.isSelected}
        isExpanded={expandedBoxKey === item.boxKey}
        isUnassigned={item.category === "unassigned"}
        hasMissingItem={
          !hasValidSelection &&
          !item.isDeleted &&
          item.category === "oos_confirmed"
        }
        hasProductSelection={hasValidSelection}
        selectedProductCount={selectedCount}
        planogramSlotNumber={
          item.category === "oos_confirmed" ? slotNumber : undefined
        }
        viewLabel={getBoxViewLabel?.(item.boxKey)}
        productImageUrls={productImageUrlsList}
        productNames={productNamesList}
        planogramName={planogramName}
        draggable={!item.isDeleted && !readOnly}
        onDragStart={handleDragStart}
        onView={() => onBoxView(item.index, item.isUserDrawn)}
        onDelete={() => onBoxDelete(item.index, item.isUserDrawn)}
        onRestore={() => onBoxRestore(item.index)}
        onClick={() => {
          onBoxSelect(item.index, item.isUserDrawn);
          setExpandedBoxKey((prev) =>
            prev === item.boxKey ? null : item.boxKey,
          );
        }}
        onRemoveSelection={() => onRemoveSelection(item.boxKey)}
        readOnly={readOnly}
      />
    );
  };

  const hasAnyBoxes = allBoxItems.length > 0 || personBoxes.length > 0;

  return (
    <div className="p-4">
      {/* Prompt when no actions taken yet */}
      {!hasAnyBoxes && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
          <Text size="sm" fw={600} c="blue" className="mb-1">
            Identify shelf issues
          </Text>
          <Text size="xs" c="dimmed">
            Use the drawing tools on the left to mark out-of-stock areas or
            empty areas on the shelf image.
          </Text>
        </div>
      )}

      {/* Out Of Stock Confirmed section */}
      <SectionHeader
        icon={<IconCrosshair size={16} color="#2563eb" />}
        label="Out Of Stock Confirmed"
        count={allOosConfirmedBoxes.length}
      />
      <DropZone
        category="oos_confirmed"
        isOver={dragOverSection === "oos_confirmed"}
        isEmpty={allOosConfirmedBoxes.length === 0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {allOosConfirmedBoxes.map(renderBoxRow)}
      </DropZone>

      {/* Empty Area section */}
      <SectionHeader
        icon={<IconSquareDashed size={16} color="#7c3aed" />}
        label="Empty Area"
        count={emptyAreaBoxes.length}
      />
      <DropZone
        category="empty_area"
        isOver={dragOverSection === "empty_area"}
        isEmpty={emptyAreaBoxes.length === 0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {emptyAreaBoxes.map(renderBoxRow)}
      </DropZone>

      {/* Unassigned section */}
      <SectionHeader
        icon={<IconQuestionMark size={16} color="#9ca3af" />}
        label="Unassigned"
        count={unassignedBoxes.length}
      />
      <DropZone
        category="unassigned"
        isOver={dragOverSection === "unassigned"}
        isEmpty={unassignedBoxes.length === 0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {unassignedBoxes.map(renderBoxRow)}
      </DropZone>

      {/* Deleted boxes (if any) */}
      {deletedBoxes.length > 0 && (
        <>
          <div className="mt-4 mb-2">
            <Text size="xs" c="dimmed" fw={600}>
              Deleted ({deletedBoxes.length})
            </Text>
          </div>
          {deletedBoxes.map(renderBoxRow)}
        </>
      )}

      {/* Unblurred Persons section */}
      <SectionHeader
        icon={<IconUser size={16} color="#f97316" />}
        label="Unblurred Person"
        count={personBoxes.length}
        className="mt-6"
      />
      {personBoxes.map(({ index }) => {
        const isHidden = hiddenUserDrawnBoxIndices.has(index);
        const isSelected = selectedUserDrawnBoxIndex === index;
        const combinedIndex = existingBoxes.length + index;

        return (
          <DetectionRow
            key={`user-person-${index}`}
            boxKey={`person-${index}`}
            viewLabel={getBoxViewLabel?.(getBoxKey(index, true))}
            displayIndex={
              boxNumberLabels.get(combinedIndex) ?? combinedIndex + 1
            }
            isAuto={false}
            isDeleted={false}
            isHidden={isHidden}
            isSelected={isSelected}
            hasMissingItem={false}
            draggable={false}
            onView={() => onBoxView(index, true)}
            onDelete={() => onBoxDelete(index, true)}
            onClick={() => onBoxSelect(index, true)}
            readOnly={readOnly}
          />
        );
      })}
    </div>
  );
};

// --- Section Header ---

const SectionHeader = ({
  icon,
  label,
  count,
  className = "",
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  className?: string;
}) => (
  <div className={`flex items-center gap-2 mb-2 ${className}`}>
    {icon}
    <Text fw={600} size="sm">
      {label} ({count})
    </Text>
  </div>
);

// --- Drop Zone ---

const DropZone = ({
  category,
  slotIndex,
  isOver,
  isEmpty,
  onDragOver,
  onDragLeave,
  onDrop,
  children,
}: {
  category: BoxCategory;
  slotIndex?: number;
  isOver: boolean;
  isEmpty: boolean;
  onDragOver: (
    e: React.DragEvent,
    category: BoxCategory,
    slotIndex?: number,
  ) => void;
  onDragLeave: () => void;
  onDrop: (
    e: React.DragEvent,
    category: BoxCategory,
    slotIndex?: number,
  ) => void;
  children: React.ReactNode;
}) => (
  <div
    className={`min-h-[32px] mb-4 rounded-md transition-colors ${
      isOver ? "bg-blue-50 border-2 border-dashed border-blue-300" : ""
    } ${isEmpty && !isOver ? "border border-dashed border-gray-200 rounded-md" : ""}`}
    onDragOver={(e) => onDragOver(e, category, slotIndex)}
    onDragLeave={onDragLeave}
    onDrop={(e) => onDrop(e, category, slotIndex)}
  >
    {isEmpty && !isOver && (
      <div className="flex items-center justify-center h-8">
        <Text size="xs" c="dimmed">
          Drag items here
        </Text>
      </div>
    )}
    {children}
  </div>
);

// --- Detection Row ---

const DetectionRow = ({
  boxKey,
  displayIndex,
  isAuto,
  isDeleted,
  isHidden,
  isSelected,
  isExpanded,
  isUnassigned = false,
  hasMissingItem,
  hasProductSelection,
  selectedProductCount = 0,
  productImageUrls = [],
  productNames = [],
  planogramName,
  planogramSlotNumber,
  viewLabel,
  draggable = false,
  onDragStart,
  onView,
  onDelete,
  onRestore,
  onClick,
  onRemoveSelection,
  readOnly,
}: {
  boxKey: string;
  displayIndex: number;
  isAuto: boolean;
  isDeleted: boolean;
  isHidden: boolean;
  isSelected: boolean;
  isExpanded?: boolean;
  isUnassigned?: boolean;
  hasMissingItem: boolean;
  hasProductSelection?: boolean;
  selectedProductCount?: number;
  productImageUrls?: string[];
  productNames?: string[];
  planogramName?: string;
  /** 1-indexed planogram slot number, shown as a map badge on OOS rows */
  planogramSlotNumber?: number;
  viewLabel?: string;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, boxKey: string) => void;
  onView: () => void;
  onDelete: () => void;
  onRestore?: () => void;
  onClick: () => void;
  onRemoveSelection?: () => void;
  readOnly?: boolean;
}) => {
  const paddedIndex = String(displayIndex).padStart(2, "0");

  return (
    <div className="mb-1">
      <div
        className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors
          ${isSelected && !isDeleted ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50"}
          ${isDeleted ? "opacity-50" : ""}
          ${hasMissingItem && !isDeleted ? "border border-orange-300 bg-orange-50" : ""}
          ${isUnassigned && !isDeleted && !isSelected ? "border border-amber-300 bg-amber-50" : ""}
          ${draggable ? "cursor-grab active:cursor-grabbing" : ""}
        `}
        draggable={draggable}
        onDragStart={draggable ? (e) => onDragStart?.(e, boxKey) : undefined}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <Text
            size="sm"
            fw={600}
            c={isDeleted ? "dimmed" : undefined}
            td={isDeleted ? "line-through" : undefined}
          >
            {paddedIndex}
          </Text>
          {isAuto && (
            <Badge size="xs" color={isDeleted ? "gray" : "blue"}>
              AUTO
            </Badge>
          )}
          {viewLabel && (
            <Badge size="xs" variant="outline" color="grape">
              {viewLabel}
            </Badge>
          )}
          {planogramSlotNumber != null && !isDeleted && (
            <Badge
              size="xs"
              variant="outline"
              color="gray"
              leftSection={<IconMap size={10} />}
            >
              {planogramSlotNumber}
            </Badge>
          )}
          {hasMissingItem && !isDeleted && (
            <Badge size="xs" color="red">
              MISSING ITEM
            </Badge>
          )}
          {hasProductSelection && !isDeleted && (
            <div className="flex items-center gap-1">
              {/* Show up to 3 product thumbnails */}
              <div className="flex -space-x-1">
                {productImageUrls.slice(0, 3).map((url, i) => (
                  <Image
                    key={i}
                    src={url}
                    alt=""
                    w={20}
                    h={20}
                    fit="contain"
                    className="rounded border border-white bg-white"
                    style={{ zIndex: 3 - i }}
                  />
                ))}
              </div>
              <Badge
                size="xs"
                color="green"
                rightSection={
                  <IconX
                    size={10}
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveSelection?.();
                    }}
                  />
                }
              >
                {selectedProductCount === 1
                  ? "SELECTED"
                  : `${selectedProductCount} SELECTED`}
              </Badge>
            </div>
          )}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1">
            {isDeleted ? (
              <ActionIcon
                size="sm"
                variant="subtle"
                color="blue"
                onClick={(e) => {
                  e.stopPropagation();
                  onRestore?.();
                }}
                title="Restore"
              >
                <IconArrowBackUp size={16} />
              </ActionIcon>
            ) : (
              <>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color={isHidden ? "red" : "gray"}
                  onClick={(e) => {
                    e.stopPropagation();
                    onView();
                  }}
                  title={isHidden ? "Show" : "Hide"}
                >
                  {isHidden ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  title="Delete"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </>
            )}
          </div>
        )}
      </div>
      {/* Expanded detail panel - show all selected products */}
      {isExpanded && hasProductSelection && !isDeleted && (
        <div className="ml-8 mt-1 mb-2 space-y-1">
          {productNames.map((name, i) => {
            const imageUrl = productImageUrls[i];
            return (
              <div
                key={i}
                className="bg-gray-50 rounded-md border border-gray-200 flex items-stretch overflow-hidden"
              >
                {imageUrl && (
                  <HoverCard
                    width={240}
                    shadow="md"
                    openDelay={300}
                    position="left"
                  >
                    <HoverCard.Target>
                      <Image
                        src={imageUrl}
                        alt={name}
                        w={56}
                        fit="contain"
                        className="bg-white border-r border-gray-200 flex-shrink-0 cursor-pointer"
                      />
                    </HoverCard.Target>
                    <HoverCard.Dropdown p="xs">
                      <div className="flex flex-col items-center gap-2">
                        <Image
                          src={imageUrl}
                          alt={name}
                          w={160}
                          h={160}
                          fit="contain"
                        />
                        <div className="w-full text-center">
                          <Text size="sm" fw={600} lineClamp={2}>
                            {name}
                          </Text>
                          {planogramName && (
                            <Text size="xs" c="dimmed" mt={2}>
                              {planogramName}
                            </Text>
                          )}
                        </div>
                      </div>
                    </HoverCard.Dropdown>
                  </HoverCard>
                )}
                <div className="flex flex-col justify-center gap-0.5 min-w-0 py-2 px-3 flex-1">
                  <Text size="xs" fw={600} lineClamp={2}>
                    {name}
                  </Text>
                  {planogramName && i === 0 && (
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {planogramName}
                    </Text>
                  )}
                </div>
              </div>
            );
          })}
          {!readOnly && (
            <div className="flex justify-end pt-1">
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                leftSection={<IconX size={12} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveSelection?.();
                }}
              >
                Clear all
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
